import type Shipment from 'interfaces/PmParcel';
import { useGlobalConfigContext } from 'GlobalConfig.context';
import { useTranslation } from 'react-i18next';
import { useCurrentShipment } from 'components/MerchantTrackingWidget/hooks/useCurrentShipment';

import Button from 'components/Button';
import {
  DEMO_TARGET,
  DEMO_TARGET_ATTRIBUTE,
} from 'constants/demoTarget.constants';
import { getIsDemoExperience } from 'services/globalConfig.services';
import { Stack } from 'components/Stack';
import HelperText from 'components/HelperText';
import LoadingDots from 'components/LoadingDots';
import { CheckmarkIcon } from 'components/CustomIcon';
import { CustomText, CustomTextDefault } from 'components/CustomText';
import { ReturnRequestModal } from 'components/MerchantTrackingWidget/components/ReturnRequestModal';
import { ConsumerValidationModal } from '../../../ConsumerValidationModal';

import { useReturnScreen } from './useReturnScreen';

import {
  Container,
  ExplainingSection,
  ReturnsIcon,
  Title,
} from './ReturnScreen.styles';

interface ReturnScreenProps {
  shipment: Shipment;
  onViewTrackingDetails: () => void;
}

export const ReturnScreen = ({
  shipment,
  onViewTrackingDetails,
}: ReturnScreenProps): JSX.Element => {
  const { t } = useTranslation();
  const { updateShipment } = useCurrentShipment();

  const {
    isLoading,
    error,
    isShipmentEligibleForReturn,
    isNotValidToReturn,
    shipmentHasReturnRequests,
    showRequestReturnModal,
    showValidateConsumerModal,
    handleProcessReturn,
    handleValidateSucceed,
    closeValidateConsumerModal,
    closeRequestReturnModal,
  } = useReturnScreen({
    shipment,
    onUpdateShipment: updateShipment,
  });

  const { config } = useGlobalConfigContext();
  const {
    return: { customText },
  } = config;

  // The demo carries no returns flow, so the CTA shows wherever it normally
  // would and does nothing when clicked.
  const isDemoExperience = getIsDemoExperience();

  const renderCTAButton = (textKey: string) => (
    <Button
      variant="outlined"
      onClick={handleProcessReturn}
      disabled={isDemoExperience || isNotValidToReturn}
      {...{ [DEMO_TARGET_ATTRIBUTE]: DEMO_TARGET.returnCta }}
    >
      <Stack align="center" gap={8}>
        <ReturnsIcon />
        {t(textKey)}
      </Stack>
    </Button>
  );

  const renderExplainingSection = () => (
    <ExplainingSection>
      <CustomText
        text={customText.orderNonEligible?.text}
        defaultText={CustomTextDefault.OrderNonEligible}
      />
    </ExplainingSection>
  );

  const renderBody = () => {
    /*
      1. if shipment has return request, show 'view return details' regardless of return eligibility
    */
    if (shipmentHasReturnRequests) {
      return renderCTAButton('RETURN_VIEW_RETURN_DETAILS_CTA');
    }

    /*
      2. if shipment does not have return requests, check if it is eligible for return
        a. if it is eligible, show return your items cta
        b. if it is not eligible, show explaining section 
    */
    if (isShipmentEligibleForReturn) {
      return renderCTAButton('RETURN_RETURN_YOUR_ITEMS_CTA');
    }

    return renderExplainingSection();
  };

  return (
    <>
      <Container data-testid="return-screen-container">
        <Stack direction="column" gap={8} justify="center" align="center">
          <CheckmarkIcon />
          <Title data-testid="return-screen-title">
            {t('SUCCESSFULLY_DELIVERED_TITLE')}
          </Title>
          {!shipmentHasReturnRequests && (
            <p>{t('SUCCESSFULLY_DELIVERED_TEXT')}</p>
          )}
        </Stack>

        {isLoading && <LoadingDots />}
        {error && <HelperText isError>{t(error)}</HelperText>}

        {!isLoading && renderBody()}

        <Button variant="link" onClick={() => onViewTrackingDetails()}>
          {t('VIEW_TRACKING_DETAILS')}
        </Button>
      </Container>

      {showValidateConsumerModal && (
        <ConsumerValidationModal
          isOpen
          shipment={shipment}
          onClose={closeValidateConsumerModal}
          onValidateSucceed={handleValidateSucceed}
        />
      )}

      {showRequestReturnModal && (
        <ReturnRequestModal
          isOpen
          shipment={shipment}
          onClose={closeRequestReturnModal}
        />
      )}
    </>
  );
};
